import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import NoticeToast from '../../components/common/NoticeToast';
import { getAllPlans } from '../../api/plans';
import { apiUrl } from '../../config/api';
import { useNotice } from '../../hooks/useNotice';
import { openOrDownloadDocument } from '../../utils/documentHandling';
import './AdminPlanDetail.css';

const FILE_BASE = apiUrl();

function safeFilename(name = '') {
  return name.replace(/[^A-Za-z0-9._-]/g, '_');
}

export default function AdminPlanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { notice, showNotice, clearNotice } = useNotice();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    const loadPlan = async () => {
      setLoading(true);
      setError('');
      try {
        const plans = await getAllPlans();
        const selected = plans.find((item) => String(item.id) === String(planId));
        if (!selected) {
          setError('Plan introuvable.');
          setPlan(null);
        } else {
          setPlan(selected);
        }
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement du plan.');
      } finally {
        setLoading(false);
      }
    };

    loadPlan();
  }, [planId]);

  const planPdfUrl = useMemo(() => {
    if (!plan?.nom_fichier) {
      return null;
    }
    return `${FILE_BASE}/static/plans/${plan.id}_${safeFilename(plan.nom_fichier)}`;
  }, [plan]);

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-plan-detail">
          <div className="admin-plan-detail-header">
            <div>
              <h1>Détails du plan de cours</h1>
              <p>Plan ID: {planId}</p>
            </div>
            <button type="button" className="btn-back-list" onClick={() => navigate('/admin/plans')}>
              Retour à la liste
            </button>
          </div>

          {loading ? (
            <div className="admin-plan-detail-card">Chargement du plan...</div>
          ) : error ? (
            <div className="admin-plan-detail-card admin-plan-error">{error}</div>
          ) : (
            <div className="admin-plan-detail-grid">
              <section className="admin-plan-detail-card">
                <h2>Informations principales</h2>
                <div className="admin-plan-fields">
                  <div><span>Cours</span><strong>{plan?.cours || '-'}</strong></div>
                  <div><span>Numéro</span><strong>{plan?.numero_cours || '-'}</strong></div>
                  <div><span>Session</span><strong>{plan?.session || '-'}</strong></div>
                  <div><span>Statut</span><strong>{plan?.statut || '-'}</strong></div>
                  <div><span>Enseignant</span><strong>{plan?.enseignant_nom || '-'}</strong></div>
                  <div><span>Chef d'équipe</span><strong>{plan?.chef_equipe_nom || '-'}</strong></div>
                  <div><span>Programme</span><strong>{plan?.programme || '-'}</strong></div>
                  <div><span>Mode</span><strong>{plan?.mode_enseignement || '-'}</strong></div>
                  <div><span>Campus</span><strong>{plan?.campus || '-'}</strong></div>
                  <div><span>Date upload</span><strong>{plan?.date_upload ? new Date(plan.date_upload).toLocaleDateString('fr-FR') : '-'}</strong></div>
                  <div><span>Fichier</span><strong>{plan?.nom_fichier || '-'}</strong></div>
                </div>
              </section>

              <section className="admin-plan-detail-card">
                <h2>Actions rapides</h2>
                <div className="admin-plan-actions">
                  <button type="button" className="btn-action" onClick={() => navigate(`/grid/${planId}`)}>
                    Ouvrir la grille
                  </button>
                  <button
                    type="button"
                    className="btn-action"
                    onClick={() => {
                      if (!planPdfUrl) {
                        showNotice('Aucun document associe a ce plan.', 'warning');
                        return;
                      }

                      openOrDownloadDocument({
                        url: planPdfUrl,
                        fileName: plan?.nom_fichier,
                        onOpenPdf: () => window.open(planPdfUrl, '_blank', 'noopener,noreferrer'),
                        onNotify: showNotice,
                      });
                    }}
                  >
                    Ouvrir le PDF
                  </button>
                </div>
                <p className="admin-plan-note">Le bouton grille ouvre la vue complète de la grille du plan.</p>
              </section>
            </div>
          )}

          <NoticeToast notice={notice} onClose={clearNotice} />
        </div>
      </div>
    </div>
  );
}
